import React, {useCallback, useEffect} from 'react';

import {
    useQueryClient,
    UseQueryResult,
    useMutation,
    useQuery,
    useInfiniteQuery,
    UseInfiniteQueryResult
} from 'react-query';
import merge from 'lodash/merge';

import {
    Survey,
    SurveySettings,
    SurveySlug,
    SurveyList,
    CopySurveyOptions,
    RevisionState,
    SurveyPreset,
    SurveyScope
} from 'client/common/types';

import {SurveyState} from 'client/common/store/reducers/survey/types';

import {useModal} from 'client/common/hooks/modal';
import {useIsSuperuser} from 'client/common/hooks/user';

import {ClientSelectModal} from 'client/common/components/client-select';

import {genSurveyLink} from 'client/common/utils/survey-link';
import {openLink} from 'client/common/utils/window';

import {
    bindSurvey,
    getSurvey,
    getSurveys,
    createSurvey,
    updateSurvey,
    copySurvey,
    updateSurveyBulk,
    denyModeration,
    getSurveyPackageInfo,
    changeSurveyCreator,
    getSurveyCreator,
    getSurveyInterviewCount,
    // createSurveyInterviewTopUpLink,
    getInterviewCost,
    updateSurveyPaymentAccount
} from 'client/common/utils/api-client';

import {genSurveysFetchParams} from 'client/common/store/saga/quicksurvey/surveys/fetch';
import {ListFilter, ListMode} from 'client/common/store/reducers/surveys/types';
import {
    CreateSurveyParams,
    GetInterviewCostParamsResponse,
    GetSurveyInterviewCountParamsResponse,
    SurveysFetchFormat
} from 'client/common/utils/api-client/types';

import {useSurveySlug} from 'client/common/contexts/slug';
import {useSurveyApiLocation} from 'client/common/contexts/survey-api-location';

import I18N from 'assets/i18n/brandlift-project';

import {getPriceKey} from '../payment';
import {onRequestSettled} from '../utils';
import {useUser} from 'client/common/contexts/user';
import {useCurrentOrg} from '../orgs';

const SURVEY_KEY = 'survey';
const SURVEYS_KEY = 'surveys';

const i18n = I18N.common;

const PRESETS_FEEDBACK = [
    SurveyPreset.YandexActiveFeedback,
    SurveyPreset.YandexPassiveFeedback,
    SurveyPreset.YandexMorda
];

export function getSurveyKey(slug: SurveySlug) {
    return [SURVEY_KEY, slug] as const;
}

export function getSurveyInterviewKey(slug: SurveySlug) {
    return [SURVEY_KEY, slug, 'interview'] as const;
}

export function getSurveyTagsKey(slug: SurveySlug) {
    return [SURVEY_KEY, slug, 'tags'] as const;
}

export function getSurveyInterviewCostKey({count}: {count: number}) {
    return [SURVEY_KEY, 'interviewCost', {count}] as const;
}

export function getSurveyPackageInfoKey(slug: SurveySlug) {
    return [SURVEY_KEY, slug, 'packages'] as const;
}

export function getSurveyCreatorKey(slug: SurveySlug) {
    return ['creator', slug] as const;
}

export function getSurveyAccountKey(slug: SurveySlug) {
    return [SURVEY_KEY, slug, 'account'] as const;
}

export function useSurvey(slug: SurveySlug, onErrorCb?: () => void): UseQueryResult<Survey> {
    const apiLocation = useSurveyApiLocation();

    return useQuery(
        getSurveyKey(slug),
        async () => {
            const {data} = await getSurvey({slug, apiLocation});

            return data;
        },
        {
            onError() {
                if (onErrorCb) {
                    onErrorCb();
                }
            }
        }
    );
}

export function useSurveySettings() {
    const slug = useSurveySlug();
    const survey = useSurvey(slug);

    return survey.data?.settings;
}

export function useSurveyScope(): SurveyScope | undefined {
    const settings = useSurveySettings();

    return settings?.scope[0];
}

export function useSurveyName() {
    const slug = useSurveySlug();
    const survey = useSurvey(slug);

    return survey?.data?.name;
}

export function useSurveyInterviewCountData(
    slug: SurveySlug
): UseQueryResult<GetSurveyInterviewCountParamsResponse> {
    return useQuery(getSurveyInterviewKey(slug), async () => {
        return getSurveyInterviewCount({slug});
    });
}

export function useSurveyPayedInterviews(slug: SurveySlug): number | undefined {
    const {status, data} = useSurveyInterviewCountData(slug);

    return status === 'success' ? data?.data.count : undefined;
}

export function useSurveyInterviewCost(
    interviewCount: number
): UseQueryResult<GetInterviewCostParamsResponse> {
    return useQuery(getSurveyInterviewCostKey({count: interviewCount}), async () => {
        return getInterviewCost({interviewCount});
    });
}

interface UseSurveysParams {
    filter: ListFilter;
    paginationKey: string | undefined;
    format: SurveysFetchFormat;
}

export function getSurveysKey({filter, format, paginationKey}: Partial<UseSurveysParams>) {
    return [SURVEYS_KEY, {format, paginationKey, ...filter}] as const;
}

export interface NextPageParams {
    paginationKey: string;
}

export function useSurveys(
    params: UseSurveysParams
): UseInfiniteQueryResult<SurveyList | undefined, NextPageParams> {
    const {filter, format, paginationKey} = params;

    const fetchParams = genSurveysFetchParams({
        filter,
        format,
        paginationKey,
        listMode: ListMode.User
    });

    return useInfiniteQuery({
        queryKey: getSurveysKey(params),
        queryFn: async ({pageParam = 0}) => {
            const {data} = await getSurveys({...fetchParams, paginationKey: pageParam});

            return data;
        },
        getNextPageParam: lastPage => {
            if (lastPage.surveys.length < fetchParams.minCount) {
                return false;
            }

            return lastPage.paginationKey;
        }
    });
}

export function useCreateSurvey() {
    const {mutateAsync} = useMutation(createSurvey, {
        onSettled: onRequestSettled
    });

    return useCallback(
        async (params: CreateSurveyParams) => {
            return mutateAsync(params);
        },
        [mutateAsync]
    );
}

function surveyListSyncSurveyStateUpdate(
    surveyList: SurveyList,
    slug: SurveySlug,
    state: SurveyState
) {
    return {
        ...surveyList,
        surveys: surveyList.surveys.map(survey => {
            if (survey.slug === slug) {
                return {
                    ...survey,
                    state
                };
            }

            return survey;
        })
    };
}

function useUpdateSurveyState(slug: SurveySlug, state: SurveyState) {
    const key = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateSurvey, {
        onMutate: async () => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => ({...old, state}));

            return shapshot;
        },
        onSuccess: () => {
            queryClient.setQueryData(SURVEYS_KEY, (old: SurveyList | undefined) => {
                if (!old) {
                    return old;
                }

                return surveyListSyncSurveyStateUpdate(old, slug, state);
            });
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        }
    });

    return useCallback(async () => {
        await mutateAsync({slug, state});
    }, [mutateAsync, slug, state]);
}

export function useSurveyPaymentAccountId(slug: SurveySlug): Survey['accountId'] {
    const survey = useSurvey(slug);

    return survey.data?.accountId;
}

export function useUpdateSurveyAccount(slug: SurveySlug) {
    const key = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateSurveyPaymentAccount, {
        onMutate: async ({accountId}) => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => ({...old, accountId}));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        }
    });

    return useCallback(
        async (accountId: number) => {
            await mutateAsync({slug, accountId});
        },
        [mutateAsync, slug]
    );
}

export function useCancelSurveyModeration(slug: SurveySlug) {
    return useUpdateSurveyState(slug, SurveyState.Draft);
}

export function useDenySurveyModeration(slug: SurveySlug) {
    const key = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(denyModeration, {
        onMutate: async () => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => ({...old, state: SurveyState.Draft}));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        }
    });

    return useCallback(
        async (withEmail?: boolean, reasons?: string[]) => {
            return mutateAsync({slugs: [slug], reasons, withEmail});
        },
        [mutateAsync, slug]
    );
}

export function useDenySurveyModerationBulk(slugs: SurveySlug[]) {
    const {mutateAsync} = useMutation(denyModeration);

    return useCallback(
        async (withEmail?: boolean, reasons?: string[]) => {
            return mutateAsync({slugs, reasons, withEmail});
        },
        [mutateAsync, slugs]
    );
}

export function useResumeSurvey(slug: SurveySlug) {
    return useUpdateSurveyState(slug, SurveyState.Active);
}

export function usePauseSurvey(slug: SurveySlug) {
    return useUpdateSurveyState(slug, SurveyState.Paused);
}

export function useUpdateSurveyName(slug: SurveySlug) {
    const key = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateSurveyBulk, {
        onMutate: async ({data: {name}}) => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => ({...old, name}));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        }
    });

    return useCallback(
        async (name: string) => {
            const params = {slug, data: {name}, apiLocation: 'survey'};

            await mutateAsync(params);
        },
        [mutateAsync, slug]
    );
}

export function useUpdateSurveySettings(slug: SurveySlug) {
    const key = getSurveyKey(slug);

    const isSuperuser = useIsSuperuser();

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateSurveyBulk, {
        onMutate: async ({data}: {data: Partial<Survey>}) => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => merge(old, data));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        },
        onSuccess: () => {
            // Вытягиваю revisionId из кэша, чтобы избежать циркулярной зависимости
            const cacheData: any = queryClient.getQueriesData(key);
            const revisionId = cacheData[0][1].revisions.find(
                (revision: {state: RevisionState}) => revision.state === RevisionState.DRAFT
            ).id;

            queryClient.invalidateQueries(['revision', {slug, revisionIndex: revisionId}]);
        }
    });

    return useCallback(
        async (settings: Partial<SurveySettings>) => {
            const params = {slug, data: {settings}, apiLocation: 'survey'};

            await mutateAsync(params, {
                onSuccess: async () => {
                    if (isSuperuser) {
                        await queryClient.refetchQueries(getSurveyKey(slug));
                    }
                }
            });
        },
        [slug, mutateAsync, isSuperuser, queryClient]
    );
}

export function useTryBindSurvey(slug: SurveySlug) {
    const user = useUser();

    const key = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(bindSurvey, {
        onMutate: async ({clientId}) => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => ({
                ...old,
                ...(clientId && {clientId}),
                isTemporary: false
            }));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        },
        onSettled: onRequestSettled
    });

    return useCallback(
        async (clientId?: string) => {
            if (user?.info?.uid) {
                await mutateAsync({slug, clientId});
            }
        },
        [mutateAsync, slug, user?.info?.uid]
    );
}

export function useBindSurveyAttempt(survey: Survey) {
    const user = useUser();

    const [showModal, hideModal] = useModal();

    const tryBindSurvey = useTryBindSurvey(survey.slug);

    useEffect(() => {
        if (!survey.isTemporary) {
            return;
        }

        if (user?.info?.authInfo.isAgency && !survey.clientId) {
            const handleClientSelect = async (clientId: string) => {
                hideModal();

                await tryBindSurvey(clientId);
            };

            showModal(<ClientSelectModal onFinish={handleClientSelect} />, {
                hideByOutsideClick: false
            });
        } else {
            void tryBindSurvey();
        }
    }, [
        survey.isTemporary,
        survey.clientId,
        showModal,
        hideModal,
        tryBindSurvey,
        user?.info?.authInfo.isAgency
    ]);
}

export function useUpdateSurveyBoxContext(slug: SurveySlug) {
    const key = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateSurveyBulk, {
        onMutate: async ({data: {box}}) => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: Survey) => ({...old, box}));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        }
    });

    return useCallback(
        async (box: unknown) => {
            const params = {slug, data: {box}, apiLocation: 'survey'};

            await mutateAsync(params);
        },
        [mutateAsync, slug]
    );
}

interface SurveyPackageCount {
    count: number;
}

export function useSurveyPackageInfo(slug: SurveySlug): UseQueryResult<SurveyPackageCount> {
    return useQuery(getSurveyPackageInfoKey(slug), async () => {
        const {data} = await getSurveyPackageInfo({slug});

        return data;
    });
}

export function useSurveyCreator(): UseQueryResult<string> {
    const slug = useSurveySlug();
    const key = getSurveyCreatorKey(slug);

    return useQuery(key, async () => {
        const {data} = await getSurveyCreator(slug);

        return data.login;
    });
}

export function useUpdateSurveyCreator() {
    const slug = useSurveySlug();
    const priceKey = getPriceKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(changeSurveyCreator, {
        onSettled: async () => {
            await queryClient.refetchQueries(priceKey);
        }
    });

    return useCallback(
        async (login: string) => {
            return mutateAsync(
                {slug, login},
                {
                    onError: err => {
                        throw err;
                    }
                }
            );
        },
        [mutateAsync, slug]
    );
}

interface CopyOptions {
    openAfterCopy: boolean;
}

export function useCopySurvey(slug: SurveySlug, name: string, copyOptions: CopyOptions) {
    const orgId = useCurrentOrg();

    const {mutateAsync} = useMutation(copySurvey, {
        onSuccess: res => {
            if (copyOptions.openAfterCopy) {
                const link = genSurveyLink({
                    slug: res.data.slug,
                    surveyType: res.data.surveyType
                });

                openLink(link);
            }
        }
    });

    return useCallback(
        async (options: CopySurveyOptions) => {
            const params = {
                slug,
                name: `${i18n.copy} ${name}`,
                ...options,
                ...(orgId && {orgId})
            };

            return mutateAsync(params);
        },
        [mutateAsync, slug, name, orgId]
    );
}

export function useSurveyPurpose(slug: SurveySlug) {
    const {data: survey} = useSurvey(slug);

    return survey?.purpose;
}

export function useIsFeedback(slug: SurveySlug) {
    const purpose = useSurveyPurpose(slug);

    return purpose && PRESETS_FEEDBACK.includes(purpose.selected);
}
